import { Delete } from '@mui/icons-material';
import { Grid, IconButton, InputAdornment, TextField, Autocomplete } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import { Field, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import StyledDropzone from 'app/components/Dropzone';
import { translations } from 'locales/i18n';

export interface Participant {
  label: string;
  value: string;
}

export const participants: Participant[] = [
  { label: 'All International Students', value: 'ALL_INTERNATIONAL_STUDENTS' },
  { label: 'Exchange Students', value: 'EXCHANGE_STUDENTS' },
  { label: 'Master International Students', value: 'MASTER_INTERNATIONAL_STUDENTS' }
];

export function EventInformation() {
  const { t } = useTranslation();

  const { values, errors, setFieldValue, touched }: any = useFormikContext();

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <Field
            as={TextField}
            name="eventName"
            error={touched.eventName && errors.eventName}
            helperText={touched.eventName && errors.eventName}
            fullWidth
            required
            label={t(translations.pages.internationalOffice.events.eventName())}
          />
        </Grid>
        <Grid item xs={6}>
          <Field
            as={TextField}
            name="eventOfficer"
            error={touched.eventOfficer && errors.eventOfficer}
            helperText={touched.eventOfficer && errors.eventOfficer}
            fullWidth
            required
            label={t(translations.pages.internationalOffice.events.eventOfficer())}
          />
        </Grid>
        <Grid item xs={6}>
          <Field
            as={TextField}
            name="eventOfficerEmail"
            error={touched.eventOfficerEmail && errors.eventOfficerEmail}
            helperText={touched.eventOfficerEmail && errors.eventOfficerEmail}
            fullWidth
            required
            label={t(translations.pages.internationalOffice.events.contact())}
          />
        </Grid>
        <Grid item xs={6}>
          <Field
            as={TextField}
            name="meetingPoint"
            error={touched.meetingPoint && errors.meetingPoint}
            helperText={touched.meetingPoint && errors.meetingPoint}
            fullWidth
            required
            label={t(translations.pages.internationalOffice.events.meetingPoint())}
          />
        </Grid>
        <Grid item xs={6}>
          <Autocomplete
            options={participants}
            value={
              values.participants &&
              values.participants.map(participant =>
                participants.find(par => par.value === participant)
              )
            }
            multiple
            autoHighlight
            fullWidth
            getOptionLabel={option => option.label}
            onChange={async (event, value, reason) => {
              setFieldValue(
                'participants',
                value.map(participant => participant.value),
                true
              );
            }}
            renderInput={params => (
              <TextField
                {...params}
                error={touched.participants && errors.participants}
                helperText={touched.participants && errors.participants}
                fullWidth
                label={t(translations.pages.internationalOffice.events.participants())}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password'
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <DateTimePicker
            className="articleModal__body__input__datepicker"
            disablePast
            renderInput={props => (
              <TextField
                {...props}
                variant="outlined"
                required
                label={t(translations.pages.internationalOffice.events.dateAndTime())}
                error={touched.eventDate && errors.eventDate}
              />
            )}
            inputFormat="dd.MM.yyyy HH:mm"
            value={values.eventDate ? values.eventDate : null}
            onChange={e => setFieldValue('eventDate', e)}
            ampm={false}
          />
        </Grid>
        <Grid item xs={3}>
          <DatePicker
            className="articleModal__body__input__datepicker"
            disablePast
            renderInput={props => (
              <TextField
                {...props}
                variant="outlined"
                required
                label={t(translations.pages.internationalOffice.events.registrationDeadline())}
                error={touched.registrationDeadline && errors.registrationDeadline}
              />
            )}
            value={values.registrationDeadline ? values.registrationDeadline : null}
            inputFormat="dd.MM.yyyy"
            onChange={e => setFieldValue('registrationDeadline', e)}
          />
        </Grid>
        <Grid item xs={6}>
          <Field
            as={TextField}
            name="costs"
            error={touched.costs && errors.costs}
            helperText={touched.costs && errors.costs}
            fullWidth
            required
            label={t(translations.pages.internationalOffice.events.costs())}
            InputProps={{
              endAdornment: <InputAdornment position="end">€</InputAdornment>
            }}
          />
        </Grid>
        <Grid item container>
          <Grid item xs>
            <StyledDropzone
              name="eventDescription"
              setFieldValue={setFieldValue}
              value={values.eventDescription}
              accept={{ 'application/pdf': ['.pdf'] }}
              placeholder={
                values.eventDescription?.name ||
                values.eventDescription ||
                t(translations.pages.internationalOffice.events.eventDescription())
              }
            />
          </Grid>
          {values.eventDescription && (
            <Grid item xs="auto" style={{ alignSelf: 'center' }}>
              <IconButton
                onClick={() => {
                  setFieldValue('eventDescription', null);
                }}
              >
                <Delete />
              </IconButton>
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
}
